import React, { useEffect, useRef, useState } from "react";
import { ImageIcon, Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import {
  listOrderItemClientReferences,
  uploadUserMediaFile,
} from "@/lib/masterOrderApi";
import {
  CLIENT_REFERENCE_ACCEPT,
  appendClientReferenceIds,
  removeClientReferenceId,
  validateClientReferenceFile,
} from "@/components/masterOrder/clientReferenceMedia.mjs";
import {
  Dialog,
  DialogContent,
  DialogTitle,
} from "@/components/ui/dialog";

function useAuthedMediaBlob(fileUrl) {
  const [blobUrl, setBlobUrl] = useState(null);
  useEffect(() => {
    if (!fileUrl) {
      setBlobUrl(null);
      return undefined;
    }
    if (/^(https?:|blob:|data:)/i.test(fileUrl)) {
      setBlobUrl(fileUrl);
      return undefined;
    }
    let cancelled = false;
    let created = null;
    const relative = fileUrl.replace(/^\/api\/?/, "/");
    api.get(relative, { responseType: "blob" })
      .then(({ data }) => {
        if (cancelled) return;
        created = URL.createObjectURL(data);
        setBlobUrl(created);
      })
      .catch(() => {
        if (!cancelled) setBlobUrl(null);
      });
    return () => {
      cancelled = true;
      if (created) URL.revokeObjectURL(created);
    };
  }, [fileUrl]);
  return blobUrl;
}

function ReferenceImage({ fileUrl, className, alt, onError }) {
  const src = useAuthedMediaBlob(fileUrl);
  if (!src) {
    return (
      <div className={`${className} grid place-items-center`} style={{ background: "#F5EBD8", color: "#B9A692" }}>
        <ImageIcon size={20} strokeWidth={1.5} />
      </div>
    );
  }
  return <img src={src} alt={alt} className={className} onError={onError} />;
}

export default function ClientReferences({
  references = [],
  itemId = null,
  editable = false,
  onChange,
  testid = "mo-client-references",
}) {
  const inputRef = useRef(null);
  const [mediaById, setMediaById] = useState({});
  const [uploading, setUploading] = useState(false);
  const [selected, setSelected] = useState(null);

  useEffect(() => {
    if (!itemId) return undefined;
    let cancelled = false;
    listOrderItemClientReferences(itemId)
      .then((media) => {
        if (cancelled) return;
        setMediaById((current) => ({
          ...current,
          ...Object.fromEntries((media || []).map((entry) => [entry.id, entry])),
        }));
      })
      .catch(() => {
        // The item card remains usable if preview hydration fails.
      });
    return () => { cancelled = true; };
  }, [itemId, references.join("|")]);

  const visible = references
    .map((id) => mediaById[id])
    .filter((entry) => entry?.file_url);

  if (!editable && references.length === 0) return null;

  const addFiles = async (event) => {
    const files = Array.from(event.target.files || []);
    if (!files.length || !onChange) return;
    const valid = [];
    for (const file of files) {
      const error = validateClientReferenceFile(file);
      if (error) toast.error(`${file.name}: ${error}`);
      else valid.push(file);
    }
    if (!valid.length) {
      if (inputRef.current) inputRef.current.value = "";
      return;
    }
    setUploading(true);
    const uploaded = [];
    try {
      for (const file of valid) {
        try {
          const media = await uploadUserMediaFile(file, { title: file.name.replace(/\.[^.]+$/, "") });
          if (!media?.id || !media?.file_url) throw new Error("Сервис не вернул ссылку на фото");
          uploaded.push(media.id);
          setMediaById((current) => ({ ...current, [media.id]: media }));
        } catch (error) {
          toast.error(`${file.name}: ${formatApiError(error)}`);
        }
      }
      if (uploaded.length) {
        onChange(appendClientReferenceIds(references, uploaded));
        toast.success(uploaded.length === 1 ? "Фото добавлено. Сохраните позицию." : `Добавлено фото: ${uploaded.length}. Сохраните позицию.`);
      }
    } finally {
      setUploading(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  const remove = (mediaId) => {
    if (!onChange) return;
    onChange(removeClientReferenceId(references, mediaId));
    if (selected?.id === mediaId) setSelected(null);
  };

  return (
    <div data-testid={testid}>
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div>
          <div className="text-[10.5px] tracking-[0.16em] uppercase" style={{ color: "#8B6F4B" }}>
            Референсы клиента
          </div>
          <div className="mt-1 text-[11.5px]" style={{ color: "#8B6F4B" }}>
            Фото и примеры, которые прислал клиент для этого изделия.
          </div>
        </div>
        {editable && (
          <>
            <input
              ref={inputRef}
              type="file"
              multiple
              accept={CLIENT_REFERENCE_ACCEPT}
              className="hidden"
              onChange={addFiles}
              data-testid={`${testid}-input`}
            />
            <button
              type="button"
              onClick={() => inputRef.current?.click()}
              disabled={uploading}
              className="h-9 px-3 rounded-xl text-[12px] inline-flex items-center gap-1.5 disabled:opacity-60"
              style={{ background: "#FFFFFF", color: "#4A3728", boxShadow: "inset 0 0 0 1px #D9C6A6" }}
              data-testid={`${testid}-add`}
            >
              <Plus size={13} strokeWidth={1.8} /> {uploading ? "Загрузка…" : "Добавить фото"}
            </button>
          </>
        )}
      </div>

      {visible.length > 0 && (
        <div className="mt-3 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2 max-w-full" data-testid={`${testid}-grid`}>
          {visible.map((media, index) => (
            <div key={media.id} className="relative min-w-0">
              <button
                type="button"
                onClick={() => setSelected(media)}
                className="block w-full aspect-[4/5] overflow-hidden rounded-lg"
                style={{ boxShadow: "inset 0 0 0 1px #EFE4D0" }}
                data-testid={`${testid}-thumbnail-${index}`}
              >
                <ReferenceImage
                  fileUrl={media.file_url}
                  alt={`Референс клиента ${index + 1}`}
                  className="w-full h-full object-cover"
                />
              </button>
              {editable && (
                <button
                  type="button"
                  onClick={() => remove(media.id)}
                  aria-label="Удалить референс"
                  className="absolute top-1.5 right-1.5 w-8 h-8 rounded-full grid place-items-center"
                  style={{ background: "rgba(255,255,255,0.92)", color: "#8A3F1E" }}
                  data-testid={`${testid}-remove-${index}`}
                >
                  <Trash2 size={13} strokeWidth={1.8} />
                </button>
              )}
            </div>
          ))}
        </div>
      )}

      {editable && references.length === 0 && (
        <div className="mt-3 rounded-lg px-3 py-4 text-center text-[11.5px]"
             style={{ background: "#F7F1E7", color: "#8B6F4B" }}>
          Пока нет фотографий
        </div>
      )}

      <Dialog open={Boolean(selected)} onOpenChange={(open) => { if (!open) setSelected(null); }}>
        <DialogContent className="w-[calc(100vw-2rem)] max-w-5xl max-h-[92dvh] p-3 bg-[#FBF5EA] border-0">
          <DialogTitle className="sr-only">Референс клиента</DialogTitle>
          {selected && (
            <div className="w-full h-[min(82dvh,900px)] grid place-items-center overflow-hidden">
              <ReferenceImage
                fileUrl={selected.file_url}
                alt="Референс клиента — крупный просмотр"
                className="block max-w-full max-h-full w-auto h-auto object-contain"
              />
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
